import React, { useMemo, useState } from "react";
import { Bot, ChevronLeft, ChevronRight, FileText, Lightbulb, Send, TriangleAlert } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { useEmployerCoach } from "./EmployerModeContext";

const TRAINING_QUICK_ACTIONS = [
  { key: "next_step", label: "Was ist jetzt mein naechster Schritt?" },
  { key: "readiness", label: "Warum ist dieser Fall noch nicht bereit?" },
  { key: "missing_documents", label: "Welche Unterlagen fehlen konkret?" },
  { key: "filing_status", label: "Erklaere mir diesen Filing-Status" },
  { key: "live_risk", label: "Was waere im Live-Betrieb kritisch?" },
  { key: "priority", label: "Welche Prioritaet hat diese Aufgabe?" },
];

const LIVE_QUICK_ACTIONS = [
  { key: "next_step", label: "Was ist jetzt mein naechster Schritt?" },
  { key: "readiness", label: "Warum ist dieser Fall noch nicht bereit?" },
  { key: "missing_documents", label: "Welche Unterlagen fehlen?" },
  { key: "filing_status", label: "Erklaere mir diesen Filing-Status" },
  { key: "biggest_blocker", label: "Was ist hier der groesste Blocker?" },
  { key: "draft_request", label: "Formuliere eine sachliche Nachforderung" },
  { key: "draft_internal_note", label: "Formuliere eine interne Notiz" },
  { key: "priority", label: "Welche Prioritaet hat diese Aufgabe?" },
];

export default function EmployerTrainingCoachPanel() {
  const {
    coachOpen,
    setCoachOpen,
    coachLoading,
    coachError,
    coachMessages,
    coachSurfaceContext,
    askEmployerCoach,
    currentRun,
    isTraining,
    isLive,
    mode,
  } = useEmployerCoach();
  const [question, setQuestion] = useState("");
  const hasStableContext = Boolean(
    coachSurfaceContext?.screen_key
    || (isTraining && currentRun?.id)
  );

  const latestMessage = coachMessages[coachMessages.length - 1] || null;
  const quickActions = isTraining ? TRAINING_QUICK_ACTIONS : LIVE_QUICK_ACTIONS;
  const summaryLine = useMemo(() => {
    if (isTraining && currentRun) {
      return `${currentRun.scenario_label || currentRun.scenario_key} · ${currentRun.current_stage_label || currentRun.current_stage}`;
    }
    return coachSurfaceContext?.screen_label || "Arbeitgeberbereich";
  }, [coachSurfaceContext, currentRun, isTraining]);

  const handleQuickAction = async (action) => {
    if (!hasStableContext) return;
    await askEmployerCoach({
      question: action.label,
      quickAction: action.key,
    });
    setCoachOpen(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const nextQuestion = question.trim();
    if (!nextQuestion || !hasStableContext) return;
    await askEmployerCoach({ question: nextQuestion, quickAction: "" });
    setQuestion("");
    setCoachOpen(true);
  };

  if (isTraining && (!currentRun || currentRun.status !== "active")) return null;

  return (
    <aside className={`employer-training-coach${coachOpen ? " is-open" : ""}`} aria-hidden={!coachOpen}>
      <button
        type="button"
        className="employer-training-coach__toggle"
        onClick={() => setCoachOpen((current) => !current)}
        aria-expanded={coachOpen}
        aria-controls="employer-training-coach-panel"
      >
        <Bot size={16} strokeWidth={2} />
        <span>{isTraining ? "Training Coach" : "Live Coach"}</span>
        {coachOpen ? <ChevronRight size={16} strokeWidth={2} /> : <ChevronLeft size={16} strokeWidth={2} />}
      </button>

      <div className="employer-training-coach__panel" id="employer-training-coach-panel">
        <div className="employer-training-coach__head">
          <div>
            <p className="employer-training-coach__kicker">{isTraining ? "GPT Training Coach" : "GPT Live Coach"}</p>
            <h3>{isTraining ? "Einordnung fuer den aktiven Trainingsfall" : "Kontextbezogene Hilfe fuer den sichtbaren Live-Fall"}</h3>
            <p>{summaryLine}</p>
          </div>
        </div>

        <div className="employer-training-coach__notice">
          <TriangleAlert size={15} strokeWidth={2} />
          <span>
            {isTraining
              ? "Der Coach erklaert den Trainingsfall, fuehrt aber keine Aktionen aus und ersetzt keine Systemlogik."
              : "Der Live Coach erklaert nur den sichtbaren Fall, fuehrt keine Aktionen aus und ersetzt nicht den Systemstatus."}
          </span>
        </div>

        <div className="employer-training-coach__quick-actions">
          {quickActions.map((action) => (
            <button
              key={action.key}
              type="button"
              className="employer-training-coach__quick-action"
              disabled={coachLoading || !hasStableContext}
              onClick={() => handleQuickAction(action)}
            >
              <Lightbulb size={14} strokeWidth={2} />
              <span>{action.label}</span>
            </button>
          ))}
        </div>

        <div className="employer-training-coach__context">
          <span>Kontext</span>
          <strong>{coachSurfaceContext?.screen_label || (isTraining ? "Employer-Trainingsmodus" : "Employer Live")}</strong>
          <p>{coachSurfaceContext?.highlighted_step || "Der Coach arbeitet nur mit dem freigegebenen sichtbaren Kontext."}</p>
        </div>

        <div className="employer-training-coach__feed">
          {!hasStableContext ? (
            <p className="employer-training-coach__empty">
              Der Coach wartet auf einen stabilen sichtbaren Kontext. Oeffne zuerst einen Arbeitgeber-Arbeitsraum oder einen konkreten Fall.
            </p>
          ) : null}
          {latestMessage ? (
            <article className="employer-training-coach__message">
              <p className="employer-training-coach__question">{latestMessage.question}</p>
              <p className="employer-training-coach__answer">{latestMessage.answer}</p>
              {latestMessage.recommended_next_step ? (
                <p className="employer-training-coach__next-step">
                  Naechster Schritt: <strong>{latestMessage.recommended_next_step}</strong>
                </p>
              ) : null}
              {latestMessage.caution ? (
                <p className="employer-training-coach__caution">{latestMessage.caution}</p>
              ) : null}
              {latestMessage.draft_text ? (
                <div className="employer-training-coach__draft">
                  <div className="employer-training-coach__draft-head">
                    <FileText size={14} strokeWidth={2} />
                    <strong>{latestMessage.draft_label || "Entwurf"}</strong>
                  </div>
                  <pre className="employer-training-coach__draft-body">{latestMessage.draft_text}</pre>
                  {latestMessage.draft_disclaimer ? (
                    <p className="employer-training-coach__draft-note">{latestMessage.draft_disclaimer}</p>
                  ) : null}
                </div>
              ) : null}
            </article>
          ) : !hasStableContext ? null : (
            <p className="employer-training-coach__empty">
              {isTraining
                ? "Stelle dem Coach eine Frage oder nutze eine Schnellhilfe. Antworten bleiben strikt innerhalb des sichtbaren Trainingsfalls."
                : "Der Live Coach bleibt zurueckhaltend im Hintergrund und antwortet nur auf den freigegebenen sichtbaren Fallkontext."}
            </p>
          )}
          {coachLoading ? <p className="employer-training-coach__empty">Coach analysiert den aktuellen sichtbaren Kontext ...</p> : null}
          {coachError ? <p className="employer-training-coach__error">{coachError}</p> : null}
        </div>

        <form className="employer-training-coach__form" onSubmit={handleSubmit}>
          <Input
            value={question}
            onChange={(event) => setQuestion(event.target.value)}
            className="dashboard-field"
            placeholder={isTraining ? "Frage zum Trainingsfall stellen" : "Frage zum sichtbaren Live-Fall stellen"}
            disabled={coachLoading || !hasStableContext}
          />
          <Button type="submit" className="dashboard-inline-button" disabled={coachLoading || !hasStableContext || !question.trim()}>
            <Send className="mr-2 h-4 w-4" />
            Fragen
          </Button>
        </form>
      </div>
    </aside>
  );
}
